import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';

const ITEMS_PER_PAGE = 10;

function CertificatesManagement() {
  const [certPage, setCertPage] = useState([]);
  const [allCerts, setAllCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([null]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { user } = useAuth();
  const [selectedCert, setSelectedCert] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [userSearchTimer, setUserSearchTimer] = useState(null);

  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [courseSearchResults, setCourseSearchResults] = useState([]);
  const [courseSearching, setCourseSearching] = useState(false);
  const [courseSearchTimer, setCourseSearchTimer] = useState(null);

  const certificatesRef = collection(db, 'certificates');

  const loadCertificatesPage = async (page = 1, cursors = pageCursors) => {
    setLoading(true);
    setError('');
    try {
      let q = query(certificatesRef, orderBy('generatedAt', 'desc'), limit(ITEMS_PER_PAGE));
      const cursor = cursors[page - 1] || null;
      if (cursor) {
        q = query(q, startAfter(cursor));
      }
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCertPage(docs);
      setLastVisible(snap.docs[snap.docs.length - 1] || null);
      setHasNextPage(snap.docs.length === ITEMS_PER_PAGE);

      if (page === cursors.length && snap.docs.length === ITEMS_PER_PAGE) {
        setPageCursors(prev => {
          const next = [...prev];
          next[page] = snap.docs[snap.docs.length - 1];
          return next;
        });
      }

      setCurrentPage(page);
    } catch (e) {
      console.error('Error loading certificates page', e);
      setError('Failed to load certificates: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllCertificates = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(query(certificatesRef, orderBy('generatedAt', 'desc')));
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllCerts(docs);
      setLoading(false);
    } catch (e) {
      console.error('Error loading all certificates', e);
      setError('Failed to load certificates: ' + e.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!user) return;
      setPageCursors([null]);
      setCurrentPage(1);
      if (searchQuery.trim()) {
        await loadAllCertificates();
      } else {
        await loadCertificatesPage(1, [null]);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (searchQuery.trim()) {
      loadAllCertificates();
    } else {
      setCurrentPage(1);
      setPageCursors([null]);
      loadCertificatesPage(1, [null]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const filteredCerts = (searchQuery.trim() ? allCerts : certPage).filter(c => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      (c.userName && c.userName.toLowerCase().includes(q)) ||
      (c.userEmail && c.userEmail.toLowerCase().includes(q)) ||
      (c.courseName && c.courseName.toLowerCase().includes(q)) ||
      (c.verificationId && c.verificationId.toLowerCase().includes(q)) ||
      (c.id && c.id.toLowerCase().includes(q))
    );
  });

  const handlePageChange = async (newPage) => {
    if (newPage < 1) return;
    if (searchQuery.trim()) {
      setCurrentPage(newPage);
      return;
    }
    if (newPage === currentPage) return;
    if (newPage > currentPage && !hasNextPage) return;
    await loadCertificatesPage(newPage);
  };

  const handleView = async (cert) => {
    setSelectedCert(cert);
  };

  const openCreateForm = () => {
    setFormData({
      verificationId: '',
      userId: '',
      userName: '',
      userEmail: '',
      courseId: '',
      courseName: '',
      completedAt: '',
      generatedAt: '',
      status: 'active'
    });
    setUserSearchQuery('');
    setUserSearchResults([]);
    setUserSearching(false);
    setCourseSearchQuery('');
    setCourseSearchResults([]);
    setCourseSearching(false);
    // preload a reasonable number of courses for dropdown selection
    (async () => {
      try {
        const coursesRef = collection(db, 'courses');
        const coursesSnap = await getDocs(query(coursesRef, orderBy('title'), limit(200)));
        const courses = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCourseSearchResults(courses);
      } catch (e) {
        console.warn('Failed to preload courses for dropdown', e);
      }
    })();
    setShowCreate(true);
  };

  // Debounced user search by email prefix
  useEffect(() => {
    if (userSearchTimer) clearTimeout(userSearchTimer);
    if (!userSearchQuery || userSearchQuery.trim().length < 2) {
      setUserSearchResults([]);
      setUserSearching(false);
      return;
    }
    setUserSearching(true);
    const timer = setTimeout(async () => {
      try {
        const q = userSearchQuery.trim();
        const usersRef = collection(db, 'users');
        const usersQuery = query(usersRef, orderBy('email'), where('email', '>=', q), where('email', '<=', q + '\uf8ff'), limit(10));
        const snap = await getDocs(usersQuery);
        const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // If no email matches, try name prefix
        if (results.length === 0) {
          const nameQuery = query(usersRef, orderBy('name'), where('name', '>=', q), where('name', '<=', q + '\uf8ff'), limit(10));
          const snap2 = await getDocs(nameQuery);
          const results2 = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
          setUserSearchResults(results2);
        } else {
          setUserSearchResults(results);
        }
      } catch (e) {
        console.error('User search failed', e);
        setUserSearchResults([]);
      } finally {
        setUserSearching(false);
      }
    }, 300);
    setUserSearchTimer(timer);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearchQuery]);

  // Debounced course search by title prefix
  useEffect(() => {
    if (courseSearchTimer) clearTimeout(courseSearchTimer);
    if (!courseSearchQuery || courseSearchQuery.trim().length < 2) {
      setCourseSearchResults([]);
      setCourseSearching(false);
      return;
    }
    setCourseSearching(true);
    const timer = setTimeout(async () => {
      try {
        const qText = courseSearchQuery.trim();
        const coursesRef = collection(db, 'courses');
        const coursesQuery = query(coursesRef, orderBy('title'), where('title', '>=', qText), where('title', '<=', qText + '\uf8ff'), limit(10));
        const snap = await getDocs(coursesQuery);
        const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCourseSearchResults(results);
      } catch (e) {
        console.error('Course search failed', e);
        setCourseSearchResults([]);
      } finally {
        setCourseSearching(false);
      }
    }, 300);
    setCourseSearchTimer(timer);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSearchQuery]);

  // Ensure courses are preloaded when opening create/edit modal (also include current course if missing)
  useEffect(() => {
    if (!showCreate) return;
    (async () => {
      try {
        if (!courseSearchResults || courseSearchResults.length === 0) {
          const coursesRef = collection(db, 'courses');
          const coursesSnap = await getDocs(query(coursesRef, orderBy('title'), limit(200)));
          const courses = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          let next = courses;
          if (formData && formData.courseId) {
            const exists = courses.some(c => c.id === formData.courseId);
            if (!exists) {
              try {
                const single = await getDoc(doc(db, 'courses', formData.courseId));
                if (single.exists()) {
                  next = [{ id: single.id, ...single.data() }, ...courses];
                }
              } catch (err) {
                console.warn('Failed to fetch specific course', err);
              }
            }
          }
          setCourseSearchResults(next);
        } else if (formData && formData.courseId) {
          const exists = courseSearchResults.some(c => c.id === formData.courseId);
          if (!exists) {
            try {
              const single = await getDoc(doc(db, 'courses', formData.courseId));
              if (single.exists()) {
                setCourseSearchResults(prev => [{ id: single.id, ...single.data() }, ...prev]);
              }
            } catch (err) {
              console.warn('Failed to fetch specific course', err);
            }
          }
        }
      } catch (e) {
        console.warn('Failed to ensure courses preloaded', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (!formData.userId || !formData.courseId) {
        setError('Please select a valid existing user and course before issuing a certificate.');
        return;
      }

      const verificationId = formData.verificationId && formData.verificationId.trim()
        ? formData.verificationId.trim()
        : `IOF-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;

      // Ensure uniqueness of verificationId
      const existing = await getDoc(doc(db, 'certificates', verificationId));
      if (existing.exists()) {
        setError('Certificate with this ID already exists. Choose another ID.');
        return;
      }

      const now = new Date().toISOString();
      const payload = {
        userId: formData.userId,
        userName: formData.userName || formData.userEmail || 'Unknown',
        userEmail: formData.userEmail || '',
        courseId: formData.courseId,
        courseName: formData.courseName || '',
        completedAt: formData.completedAt || now,
        generatedAt: formData.generatedAt || now,
        verificationId: verificationId,
        status: formData.status || 'active'
      };

      // Create certificate document
      await setDoc(doc(db, 'certificates', verificationId), payload);

      // Update user's completedSubCourses to reference this certificate
      try {
        await setDoc(doc(db, 'users', formData.userId, 'completedSubCourses', formData.courseId), {
          verificationId: verificationId,
          firstCompletedAt: payload.completedAt
        }, { merge: true });
      } catch (e) {
        console.warn('Failed to update user completedSubCourses', e);
      }

      // Add history entry
      await updateDoc(doc(db, 'certificates', verificationId), {
        history: arrayUnion({
          action: 'created',
          adminId: user?.uid || null,
          adminName: user?.displayName || user?.email || null,
          timestamp: new Date().toISOString()
        })
      });

      setShowCreate(false);
      setFormData({});
      // Refresh
      if (searchQuery.trim()) await loadAllCertificates(); else await loadCertificatesPage(currentPage, pageCursors);
    } catch (e) {
      console.error('Error creating certificate', e);
      setError('Failed to create certificate: ' + e.message);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (!selectedCert || !selectedCert.id) return;
      const certRef = doc(db, 'certificates', selectedCert.id);

      // Capture previous snapshot for history
      const prev = (await getDoc(certRef)).data() || {};

      const updates = {
        userName: selectedCert.userName,
        userEmail: selectedCert.userEmail,
        courseName: selectedCert.courseName,
        completedAt: selectedCert.completedAt,
        generatedAt: selectedCert.generatedAt,
        status: selectedCert.status
      };

      await updateDoc(certRef, {
        ...updates,
        history: arrayUnion({
          action: 'edited',
          adminId: user?.uid || null,
          adminName: user?.displayName || user?.email || null,
          timestamp: new Date().toISOString(),
          previous: prev
        })
      });

      // Refresh
      if (searchQuery.trim()) await loadAllCertificates(); else await loadCertificatesPage(currentPage, pageCursors);
      setSelectedCert(null);
    } catch (e) {
      console.error('Error editing certificate', e);
      setError('Failed to edit certificate: ' + e.message);
    }
  };

  const handleRevokeRestore = async (cert, action) => {
    try {
      const reason = window.prompt(`${action === 'revoke' ? 'Revoke' : 'Restore'} certificate ${cert.verificationId}. Enter reason (optional):`);
      const certRef = doc(db, 'certificates', cert.id || cert.verificationId);
      const newStatus = action === 'revoke' ? 'revoked' : 'active';
      await updateDoc(certRef, {
        status: newStatus,
        history: arrayUnion({
          action: action,
          adminId: user?.uid || null,
          adminName: user?.displayName || user?.email || null,
          reason: reason || null,
          timestamp: new Date().toISOString()
        })
      });

      if (searchQuery.trim()) await loadAllCertificates(); else await loadCertificatesPage(currentPage, pageCursors);
    } catch (e) {
      console.error('Error revoking/restoring certificate', e);
      setError('Failed to update certificate status: ' + e.message);
    }
  };

  if (loading) return <LoadingIndicator />;

  return (
    <div>
      <section className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold flex items-center gap-3">
            Certificate Management
          </h2>
          <div className="flex items-center gap-3">
            <button onClick={openCreateForm} className="bg-[rgb(130,88,18)] text-white px-4 py-2 rounded">Issue Certificate</button>
          </div>
        </div>

        <div className="flex gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, course, ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 p-2 border rounded"
          />
        </div>

        {error && <div className="mb-4 text-red-600">{error}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issued</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCerts.map(cert => (
                <tr key={cert.id || cert.verificationId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{cert.verificationId || cert.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{cert.userName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{cert.userEmail || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{cert.courseName || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{cert.generatedAt ? new Date(cert.generatedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{cert.status || 'active'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleView(cert)} className="px-2 py-1 bg-gray-100 rounded">View</button>
                      <button onClick={() => { setSelectedCert(cert); setShowCreate(true); setFormData({ ...cert }); }} className="px-2 py-1 bg-yellow-100 rounded">Edit</button>
                      {cert.status !== 'revoked' ? (
                        <button onClick={() => handleRevokeRestore(cert, 'revoke')} className="px-2 py-1 bg-red-100 rounded">Revoke</button>
                      ) : (
                        <button onClick={() => handleRevokeRestore(cert, 'restore')} className="px-2 py-1 bg-green-100 rounded">Restore</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-700">Showing {filteredCerts.length} certificates</div>
          <div className="flex items-center space-x-2">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-[rgb(130,88,18)] text-white'}`}>Previous</button>
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-700">Page</span>
              <input type="number" min="1" value={currentPage} onChange={(e) => handlePageChange(parseInt(e.target.value || '1', 10))} className="w-16 px-2 py-1 border rounded text-center" />
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={!hasNextPage && !searchQuery.trim()} className={`px-3 py-1 rounded ${!hasNextPage && !searchQuery.trim() ? 'bg-gray-100 text-gray-400' : 'bg-[rgb(130,88,18)] text-white'}`}>Next</button>
          </div>
        </div>
      </section>

      {/* View Modal */}
      {selectedCert && !showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg max-w-[1100px] w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Certificate Preview</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => { setShowCreate(true); setFormData({ ...selectedCert }); }} className="px-3 py-1 bg-yellow-100 rounded">Edit</button>
                <button onClick={() => setSelectedCert(null)} className="px-3 py-1 bg-gray-100 rounded">Close</button>
              </div>
            </div>
            <div className="overflow-auto">
              <CertificatePreview cert={selectedCert} />
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold mb-4">{formData && formData.verificationId ? 'Edit Certificate' : 'Issue Certificate'}</h3>
            <form onSubmit={selectedCert ? handleEditSubmit : handleCreateSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm">Verification ID</label>
                  <input value={formData.verificationId || ''} onChange={(e) => setFormData({ ...formData, verificationId: e.target.value })} className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm">Status</label>
                  <select value={formData.status || 'active'} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full p-2 border rounded">
                    <option value="active">Active</option>
                    <option value="revoked">Revoked</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm">User (search by email or name)</label>
                  {!formData.userId ? (
                    <div className="relative">
                      <input
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        placeholder="Type email or name..."
                        className="w-full p-2 border rounded"
                      />
                      {userSearching && <div className="text-xs text-gray-500 mt-1">Searching...</div>}
                      {userSearchResults.length > 0 && (
                        <ul className="absolute z-40 bg-white border rounded w-full mt-1 max-h-48 overflow-auto">
                          {userSearchResults.map(u => (
                            <li key={u.id}>
                              <button type="button" onClick={() => {
                                setFormData({ ...formData, userId: u.id, userName: u.name || u.displayName || '', userEmail: u.email || '' });
                                setUserSearchQuery('');
                                setUserSearchResults([]);
                              }} className="w-full text-left p-2 hover:bg-gray-100">
                                <div className="text-sm font-medium">{u.name || u.displayName || '—'}</div>
                                <div className="text-xs text-gray-500">{u.email}</div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {userSearchResults.length === 0 && userSearchQuery.trim().length >= 2 && !userSearching && (
                        <div className="mt-1 text-sm text-gray-500">No users found</div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 p-2 border rounded">
                      <div>
                        <div className="text-sm font-medium">{formData.userName || '—'}</div>
                        <div className="text-xs text-gray-500">{formData.userEmail || '—'}</div>
                      </div>
                      <div>
                        <button type="button" onClick={() => setFormData({ ...formData, userId: '', userName: '', userEmail: '' })} className="text-sm text-red-600">Clear</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm">Course (select)</label>
                  <div>
                    <select
                      value={formData.courseId || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        const found = courseSearchResults.find(c => c.id === id);
                        if (found) setFormData({ ...formData, courseId: found.id, courseName: found.title || '' });
                        else setFormData({ ...formData, courseId: id, courseName: '' });
                      }}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">-- Select course --</option>
                      {courseSearchResults.map(c => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>
                    {courseSearchResults.length === 0 && (
                      <div className="text-sm text-gray-500 mt-1">No courses available for selection</div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm">Completed At</label>
                  <input type="datetime-local" value={formData.completedAt ? new Date(formData.completedAt).toISOString().slice(0,16) : ''} onChange={(e) => setFormData({ ...formData, completedAt: e.target.value })} className="w-full p-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm">Generated At</label>
                  <input type="datetime-local" value={formData.generatedAt ? new Date(formData.generatedAt).toISOString().slice(0,16) : ''} onChange={(e) => setFormData({ ...formData, generatedAt: e.target.value })} className="w-full p-2 border rounded" />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setShowCreate(false); setSelectedCert(null); setFormData({}); }} className="px-4 py-2 bg-gray-100 rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded">Save</button>
              </div>
            </form>
            {error && <div className="mt-2 text-red-600">{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificatesManagement;

// Certificate preview component reused in admin view
function CertificatePreview({ cert }) {
  const verifyUrl = cert?.verificationId ? `https://iofellowship.org/verify/${cert.verificationId}` : '';
  return (
    <div className="min-h-[747px] flex justify-center p-4">
      <div id="certificate-print" className="bg-white relative" style={{ width: '1056px', height: '747px', padding: '40px 60px', border: '2px solid transparent', borderImage: 'linear-gradient(90deg, #c19a6b, #825812) 1' }}>
        <img src="/images/logo.jpg" alt="Watermark" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '60%', opacity: 0.05, pointerEvents: 'none' }} crossOrigin="anonymous" />
        <img src="/images/logo.jpg" alt="Logo" style={{ position: 'absolute', top: '32px', left: '32px', width: '150px', zIndex: 10 }} crossOrigin="anonymous" />

        <div className="h-full flex flex-col justify-center items-center">
          <div className="text-center mb-8">
            <div style={{ border: '1px solid rgb(130,88,18)', padding: '1rem 2rem', backgroundColor: '#f9f9f9' }}>
              <h2 className="text-base font-serif text-gray-600">VERIFIED CERTIFICATE</h2>
              <div className="mt-0.5 text-sm" style={{ color: 'rgb(130,88,18)' }}>INTELLECTUAL OASIS FELLOWSHIP</div>
            </div>
          </div>

          <div className="text-center space-y-4 max-w-2xl">
            <div className="text-sm font-serif tracking-wide text-gray-600">
              {cert?.completedAt ? new Date(cert.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
            </div>
            <h2 className="text-5xl font-serif font-extrabold text-gray-800">{cert?.userName}</h2>
            <p className="text-lg text-gray-600">has successfully completed with distinction</p>
            <h3 className="text-2xl italic tracking-wide font-semibold text-[rgb(130,88,18)]">{cert?.courseName}</h3>
            <p className="text-base text-gray-600">a curated online course offered and compiled by Intellectual Oasis Fellowship</p>
          </div>

          <div className="flex justify-between items-end w-full mt-12">
            <div className="text-center">
              <div className="mb-2">
                <img src="/images/signature.png" alt="Signature" className="h-10 mx-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
              <div style={{ borderTop: '1px solid #333', width: '120px' }}></div>
              <p className="text-xs text-gray-600 mt-1">Olalekan L. Adeyinka</p>
              <p className="text-xs text-gray-600">Director, Intellectual Oasis Fellowship</p>
            </div>

            <div className="text-center">
              <div className="w-[110px] aspect-square mb-2 mx-auto">
                <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
                  <defs>
                    <radialGradient id="goldGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#fff4d6"/>
                      <stop offset="60%" stopColor="#c19a6b"/>
                      <stop offset="100%" stopColor="#825812"/>
                    </radialGradient>
                  </defs>
                  <circle cx="60" cy="60" r="55" fill="url(#goldGrad)" stroke="#825812" strokeWidth="2"/>
                  <circle cx="60" cy="60" r="45" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="2"/>
                  <text x="60" y="54" textAnchor="middle" fill="#4b3410" fontSize="24" fontFamily="serif">IOF</text>
                  <text x="60" y="74" textAnchor="middle" fill="#4b3410" fontSize="14" fontFamily="serif">Verified</text>
                </svg>
              </div>
              {verifyUrl && (
                <a href={verifyUrl} target="_blank" rel="noreferrer" className="text-xs text-gray-600 hover:underline hover:text-[rgb(130,88,18)]">Verify at iofellowship.org/verify/</a>
              )}
              <p className="text-[10px] text-gray-500">Certificate ID: {cert?.verificationId || cert?.id}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
